import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOpsAuth } from '@/ops/OpsAuthContext';
import { ArrowLeft, ShieldOff, ShieldCheck, FileText, Loader2, Save, CreditCard, Trash2, Settings2 } from 'lucide-react';

const PLANS = ['free', 'pro', 'team'];

export default function OpsUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { opsAxios, OPS_API, opsUser } = useOpsAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [accessRequested, setAccessRequested] = useState(false);
  // Management + billing
  const [edit, setEdit] = useState({ name: '', company: '', role: '', onboarding_completed: false, monthly_card_limit: '', monthly_email_limit: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [billing, setBilling] = useState(null);
  const [plan, setPlan] = useState('pro');
  const [planMsg, setPlanMsg] = useState('');
  const [deleteText, setDeleteText] = useState('');
  const isSuper = opsUser?.role === 'super_admin';
  const canBill = isSuper || opsUser?.role === 'billing_admin';

  const load = async () => {
    setLoading(true);
    try {
      const res = await opsAxios({ method: 'GET', url: `${OPS_API}/users/${userId}` });
      setUser(res.data);
      setEdit({
        name: res.data.name || '', company: res.data.company || '', role: res.data.role || '',
        onboarding_completed: !!res.data.onboarding_completed,
        monthly_card_limit: res.data.monthly_card_limit ?? '', monthly_email_limit: res.data.monthly_email_limit ?? '',
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadBilling = async () => {
    if (!canBill) return;
    try {
      const res = await opsAxios({ method: 'GET', url: `${OPS_API}/billing/${userId}` });
      setBilling(res.data.billing);
      if (res.data.billing?.plan) setPlan(res.data.billing.plan);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); loadBilling(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        name: edit.name, company: edit.company, role: edit.role,
        onboarding_completed: edit.onboarding_completed,
      };
      if (edit.monthly_card_limit !== '') payload.monthly_card_limit = Number(edit.monthly_card_limit);
      if (edit.monthly_email_limit !== '') payload.monthly_email_limit = Number(edit.monthly_email_limit);
      await opsAxios({ method: 'PUT', url: `${OPS_API}/users/${userId}`, data: payload });
      await load();
      setSavedMsg('Saved ✓');
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) { console.error(e); setSavedMsg('Save failed'); setTimeout(() => setSavedMsg(''), 2500); }
    finally { setSavingEdit(false); }
  };

  const handleChangePlan = async (action) => {
    setActionLoading(true); setPlanMsg('');
    try {
      const res = await opsAxios({ method: 'POST', url: `${OPS_API}/users/${userId}/plan`, data: { plan, action } });
      setPlanMsg(res.data.message || 'Done');
      await loadBilling();
    } catch (e) {
      setPlanMsg(e?.response?.data?.detail || 'Plan change failed');
    } finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await opsAxios({ method: 'DELETE', url: `${OPS_API}/users/${userId}`, params: { reason: 'Erasure request via /ops' } });
      navigate('/ops/users');
    } catch (e) { console.error(e); setActionLoading(false); }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) return;
    setActionLoading(true);
    try {
      await opsAxios({ method: 'POST', url: `${OPS_API}/users/${userId}/suspend`, data: { reason: suspendReason } });
      setShowSuspendForm(false);
      setSuspendReason('');
      await load();
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await opsAxios({ method: 'POST', url: `${OPS_API}/users/${userId}/reactivate` });
      await load();
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  };

  const handleRequestAccess = async () => {
    setActionLoading(true);
    try {
      await opsAxios({ method: 'POST', url: `${OPS_API}/access/request`, data: { user_id: userId, reason: 'Support request from /ops' } });
      setAccessRequested(true);
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  };

  if (loading) return <div className="ops-sub">Loading…</div>;
  if (!user) return <div className="text-sm" style={{ color: '#F87171' }}>User not found</div>;

  const Field = ({ label, value }) => (
    <div>
      <p className="ops-overline mb-1">{label}</p>
      <p className="text-sm" style={{ color: 'var(--ops-text)' }}>{value}</p>
    </div>
  );
  const Card = ({ title, icon: Icon, children, danger, testid }) => (
    <div className="ops-card p-5" data-testid={testid} style={danger ? { borderColor: 'rgba(127,29,29,.5)' } : undefined}>
      <p className="ops-overline mb-4 flex items-center gap-1.5" style={danger ? { color: '#F87171' } : undefined}>
        {Icon && <Icon size={12} />}{title}
      </p>
      {children}
    </div>
  );

  return (
    <div data-testid="ops-user-detail-page">
      <button onClick={() => navigate('/ops/users')} className="ops-nav inline-flex mb-5 px-0" style={{ background: 'transparent' }}>
        <ArrowLeft size={14} /> Back to Users
      </button>

      {/* Profile header */}
      <div className="ops-card p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-md flex items-center justify-center" style={{ background: 'var(--ops-surface-2)', border: '1px solid var(--ops-border)' }}>
            <span style={{ fontFamily: "'Work Sans'", fontSize: 18, fontWeight: 600, color: 'var(--ops-text)' }}>{(user.name || '?').charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <h1 className="ops-h2">{user.name}</h1>
            <p className="ops-mono text-xs" style={{ color: 'var(--ops-muted)' }}>{user.email}</p>
          </div>
        </div>
        <span className={`ops-badge ${user.is_suspended ? 'ops-badge-danger' : 'ops-badge-success'}`}>{user.is_suspended ? 'Suspended' : 'Active'}</span>
      </div>

      {/* Privacy banner */}
      <div className="ops-card flex items-center gap-2 px-4 py-2.5 mb-5 text-xs" style={{ background: 'var(--ops-input)' }}>
        <FileText size={13} style={{ color: 'var(--ops-muted)' }} />
        <span style={{ color: 'var(--ops-text-2)' }}>Viewing metadata only. Action Card content, notes, and emails are not accessible without user consent.</span>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {/* Left: profile + usage */}
        <div className="md:col-span-2 space-y-5">
          <Card title="Account">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" value={user.name} />
              <Field label="Email" value={user.email} />
              <Field label="User ID" value={<span className="ops-mono text-xs">{user.id?.slice(0, 16)}…</span>} />
              <Field label="Vertical" value={user.vertical || '—'} />
              <Field label="Joined" value={new Date(user.created_at).toLocaleDateString('en-US', { dateStyle: 'medium' })} />
              <Field label="Onboarded" value={user.onboarding_completed ? 'Yes' : 'No'} />
              <Field label="Comm. style" value={user.communication_style || '—'} />
              <Field label="Limits" value={<span className="ops-mono text-xs">{user.monthly_card_limit ?? '∞'} cards · {user.monthly_email_limit ?? '∞'} emails</span>} />
              <Field label="Country" value={user.country || '—'} />
              <Field label="Data region" value={
                <span className="ops-mono text-xs" data-testid="ops-user-region">
                  {user.home_region || <span style={{ color: 'var(--ops-muted)' }}>unset</span>}
                </span>
              } />
            </div>
          </Card>

          <Card title="Usage Metrics — no content">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Action Cards', value: user.usage?.action_cards ?? 0 },
                { label: 'Total Tasks', value: user.usage?.tasks_total ?? 0 },
                { label: 'Tasks Done', value: user.usage?.tasks_completed ?? 0 },
                { label: 'Contacts', value: user.usage?.contacts ?? 0 },
              ].map(({ label, value }) => (
                <div key={label} className="ops-card p-3.5 text-center" style={{ background: 'var(--ops-input)' }}>
                  <p className="ops-kpi-value" style={{ fontSize: '1.5rem' }}>{value}</p>
                  <p className="ops-overline mt-1.5">{label}</p>
                </div>
              ))}
            </div>
            {user.usage?.last_activity && (
              <p className="ops-sub mt-3">Last activity: {new Date(user.usage.last_activity).toLocaleDateString('en-US', { dateStyle: 'medium' })}</p>
            )}
          </Card>
        </div>

        {/* Right: actions */}
        <div className="space-y-4">
          {/* Status */}
          <Card title="Account Status">
            {user.is_suspended ? (
              <button onClick={handleReactivate} disabled={actionLoading} className="ops-btn w-full h-10" style={{ background: 'rgba(6,78,59,.4)', color: '#34D399', border: '1px solid rgba(4,120,87,.5)' }}>
                {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}Reactivate Account
              </button>
            ) : !showSuspendForm ? (
              <button onClick={() => setShowSuspendForm(true)} className="ops-btn ops-btn-danger w-full h-10">
                <ShieldOff size={14} />Suspend Account
              </button>
            ) : (
              <div className="space-y-2">
                <textarea value={suspendReason} onChange={e => setSuspendReason(e.target.value)} placeholder="Reason for suspension (required)"
                  className="ops-input w-full p-2.5 resize-none text-sm" style={{ height: 72 }} />
                <div className="flex gap-2">
                  <button onClick={handleSuspend} disabled={actionLoading || !suspendReason.trim()} className="ops-btn ops-btn-danger flex-1 h-9">{actionLoading ? 'Suspending…' : 'Confirm'}</button>
                  <button onClick={() => { setShowSuspendForm(false); setSuspendReason(''); }} className="ops-btn ops-btn-secondary h-9 px-3">Cancel</button>
                </div>
              </div>
            )}
          </Card>

          {/* Support Access */}
          <Card title="Support Access">
            <p className="ops-sub mb-3 -mt-2">Requires user consent. Sends a notification and logs all access.</p>
            {accessRequested ? (
              <div className="ops-badge ops-badge-info w-full justify-center py-2" style={{ borderRadius: 6 }}>Request sent — awaiting approval</div>
            ) : (
              <button onClick={handleRequestAccess} disabled={actionLoading} className="ops-btn ops-btn-secondary w-full h-10">Request Account Access</button>
            )}
          </Card>

          {/* Account Management */}
          <Card title="Manage Account" icon={Settings2} testid="ops-user-manage">
            <div className="space-y-2.5 -mt-1">
              {[{ k: 'name', label: 'Name' }, { k: 'company', label: 'Company' }, { k: 'role', label: 'Role / title' }].map(({ k, label }) => (
                <div key={k}>
                  <label className="ops-overline">{label}</label>
                  <input value={edit[k]} onChange={e => setEdit(p => ({ ...p, [k]: e.target.value }))} data-testid={`ops-edit-${k}`} className="ops-input w-full h-9 px-2.5 text-sm mt-1" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2">
                {[{ k: 'monthly_card_limit', label: 'Card limit/mo' }, { k: 'monthly_email_limit', label: 'Email limit/mo' }].map(({ k, label }) => (
                  <div key={k}>
                    <label className="ops-overline">{label}</label>
                    <input type="number" value={edit[k]} onChange={e => setEdit(p => ({ ...p, [k]: e.target.value }))} placeholder="∞" className="ops-input w-full h-9 px-2.5 text-sm mt-1" />
                  </div>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer pt-1" style={{ color: 'var(--ops-text-2)' }}>
                <input type="checkbox" checked={edit.onboarding_completed} data-testid="ops-edit-onboarding" onChange={e => setEdit(p => ({ ...p, onboarding_completed: e.target.checked }))} />
                Onboarding completed
              </label>
              <button onClick={handleSaveEdit} disabled={savingEdit} data-testid="ops-save-user" className="ops-btn ops-btn-primary w-full h-9 mt-1">
                {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}Save changes
              </button>
              {savedMsg && <p data-testid="ops-save-msg" className="ops-mono text-[11px] text-center mt-1" style={{ color: savedMsg.includes('failed') ? '#F87171' : '#34D399' }}>{savedMsg}</p>}
            </div>
          </Card>

          {/* Billing */}
          {canBill && (
            <Card title="Billing" icon={CreditCard} testid="ops-user-billing">
              {billing && !billing.connected ? (
                <p className="ops-badge ops-badge-warning w-full justify-center py-2 mb-3" style={{ borderRadius: 6 }}>Stripe not connected</p>
              ) : (
                <div className="text-xs mb-3 space-y-1.5 -mt-1" style={{ color: 'var(--ops-text-2)' }}>
                  <div className="flex justify-between"><span>Plan</span><span className="ops-mono" style={{ color: 'var(--ops-text)' }}>{billing?.plan || 'free'}</span></div>
                  <div className="flex justify-between"><span>Status</span><span className="ops-mono">{billing?.status || '—'}</span></div>
                  {billing?.subscription?.mrr != null && <div className="flex justify-between"><span>MRR</span><span className="ops-mono" style={{ color: 'var(--ops-text)' }}>${billing.subscription.mrr}</span></div>}
                </div>
              )}
              <div className="flex gap-2">
                <select value={plan} onChange={e => setPlan(e.target.value)} data-testid="ops-plan-select" className="ops-input flex-1 h-9 px-2 text-sm">
                  {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <button onClick={() => handleChangePlan('set')} disabled={actionLoading} data-testid="ops-set-plan" className="ops-btn ops-btn-secondary h-9 px-3.5">Set</button>
              </div>
              <button onClick={() => handleChangePlan('cancel')} disabled={actionLoading} className="ops-btn ops-btn-secondary w-full h-8 mt-2 text-xs">Cancel subscription</button>
              {planMsg && <p className="ops-mono text-[11px] mt-2" style={{ color: 'var(--ops-muted)' }}>{planMsg}</p>}
            </Card>
          )}

          {/* Danger zone */}
          {isSuper && (
            <Card title="Danger Zone" icon={Trash2} danger testid="ops-danger-zone">
              <p className="ops-sub mb-2.5 -mt-1">Permanently delete this user and all data (cards, tasks, contacts, billing). Type <strong style={{ color: 'var(--ops-text)' }}>DELETE</strong> to confirm.</p>
              <input value={deleteText} onChange={e => setDeleteText(e.target.value)} placeholder="DELETE" data-testid="ops-delete-confirm-input"
                className="ops-input w-full h-9 px-2.5 text-sm mb-2" style={{ borderColor: 'rgba(127,29,29,.5)' }} />
              <button onClick={handleDelete} disabled={actionLoading || deleteText !== 'DELETE'} data-testid="ops-delete-user" className="ops-btn ops-btn-danger w-full h-9">
                {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}Permanently delete
              </button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
